import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { DateSpan } from '../date-span.js';
import { LocalizedRichDescription } from '../localized-rich-description.js';
import { LocalizedString } from '../localized-string.js';
import { Price } from '../price.js';
export class TicketTemplateRef extends DataModel {
  static readonly TYPE_ID: string = '4e7f1770d6ef4e22825a1d94d4c77470';

  constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
    public readonly richDescriptions: LocalizedRichDescription,
    public readonly dateSpan: DateSpan,

    // Selling price of the ticket.
    public readonly price: Price,

    // If there is an offer on the ticket this would be the original price before the offer was applied for the final
    // selling price. When the user applies a discount code, this is the selling price before the discount is applied.
    public readonly originalPrice: Price | null,
  ) {
    super();
  }

  get $typeId(): string {
    return TicketTemplateRef.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketTemplateRef {
    return new TicketTemplateRef(
        MapUtils.get<string>(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
        context.fromData<LocalizedRichDescription>(MapUtils.get<SerializedData>(map, 'richDescriptions')),
        context.fromData<DateSpan>(MapUtils.get<SerializedData>(map, 'dateSpan')),
        Price.decode(MapUtils.get<string>(map, 'price')),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'originalPrice')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'names': context.toData(this.names),
      'richDescriptions': context.toData(this.richDescriptions),
      'dateSpan': context.toData(this.dateSpan),
      'price': this.price.encode(),
      'originalPrice': this.originalPrice?.encode() ?? null,
    };
  }

  clone(): this {
    return new TicketTemplateRef(
        this.uniqueId,
        this.names.clone(),
        this.richDescriptions.clone(),
        this.dateSpan.clone(),
        this.price.clone(),
        this.originalPrice?.clone() ?? null,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      isSameData(other.names, this.names) &&
      isSameData(other.richDescriptions, this.richDescriptions) &&
      isSameData(other.dateSpan, this.dateSpan) &&
      isSameData(other.price, this.price) &&
      isSameData(other.originalPrice, this.originalPrice);
  }
}

export class TicketTemplateRefBuilder extends DataModelBuilder<TicketTemplateRef> {
  build(context: SerializationContext, map: SerializedData): TicketTemplateRef {
    return TicketTemplateRef.fromMap(context, map);
  }
}
